import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, Bell, BellOff, Check, Clock3, LockKeyhole } from "lucide-react";
import { api } from "../api";
import type { OpenEvent } from "../types";

export function StatusScreen() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [events, setEvents] = useState<OpenEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationConfigured, setNotificationConfigured] = useState(false);

  useEffect(() => {
    api.statusSession()
      .then(({ authenticated: value }) => setAuthenticated(value))
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    api.opens()
      .then(({ events: value, notificationConfigured: configured }) => {
        setEvents(value);
        setNotificationConfigured(configured);
      })
      .finally(() => setLoading(false));
  }, [authenticated]);

  async function login(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api.statusLogin(code.trim());
      setAuthenticated(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "הקוד אינו נכון");
    }
  }

  if (authenticated === null) {
    return <main className="status-screen"><p>טוען…</p></main>;
  }

  if (!authenticated) {
    return (
      <main className="status-screen status-login">
        <a href="/" className="back-link"><ArrowRight aria-hidden="true" /> חזרה לאתר</a>
        <section aria-labelledby="status-login-title">
          <LockKeyhole aria-hidden="true" />
          <h1 id="status-login-title">האזור של אבי</h1>
          <p>סטטוס הפתיחות מוגן בקוד נפרד.</p>
          <form onSubmit={login}>
            <label htmlFor="status-code">קוד סטטוס</label>
            <input
              id="status-code"
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            {error && <p className="form-error" role="alert">{error}</p>}
            <button className="primary-button" type="submit">הצג סטטוס פתיחות</button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="status-screen">
      <a href="/" className="back-link"><ArrowRight aria-hidden="true" /> חזרה לאתר</a>
      <h1>פתיחת הפרקים</h1>
      <p>נשמרים רק מספר הפרק, התאריך ושעת הפתיחה.</p>
      <div className={`notification-status ${notificationConfigured ? "is-connected" : "is-pending"}`}>
        {notificationConfigured ? <Bell aria-hidden="true" /> : <BellOff aria-hidden="true" />}
        <div>
          <strong>{notificationConfigured ? "התראות פרטיות פעילות" : "התראה חיצונית ממתינה לחיבור"}</strong>
          <span>{notificationConfigured ? "אבי יקבל התראה בפתיחה הראשונה של כל מכתב." : "הפתיחות עדיין נשמרות כאן; נדרש יעד Webhook כדי לשלוח התראה מיידית."}</span>
        </div>
      </div>
      {loading ? <p>טוען…</p> : (
        <ol className="status-list">
          {Array.from({ length: 10 }, (_, index) => index + 1).map((number) => {
            const event = events.find((item) => item.dayNumber === number);
            return (
              <li key={number} className={event ? "opened" : "pending"}>
                <span>{event ? <Check aria-hidden="true" /> : <Clock3 aria-hidden="true" />}</span>
                <div>
                  <strong>יום {number}{number === 10 ? " — החתונה" : ""}</strong>
                  <small>{event ? new Intl.DateTimeFormat("he-IL", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Jerusalem" }).format(new Date(event.openedAt)) : "עדיין לא נפתח"}</small>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </main>
  );
}
